import { useCallback, useEffect, useState } from "react";
import api from "../services/api";
import { isFreemiumPaywallError, normalizeFreemiumStatus } from "../utils/freemiumAccess";

export function useFreemiumAccess({ enabled = true } = {}) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paywallOpen, setPaywallOpen] = useState(false);

  const refresh = useCallback(async () => {
    if (!enabled || !api.getToken?.()) {
      setStatus(null);
      return null;
    }
    if (api.getAuthRoleFromToken?.() && api.getAuthRoleFromToken() !== "CUSTOMER") {
      setStatus(null);
      return null;
    }
    setLoading(true);
    try {
      const res = await api.getFreemiumStatus();
      const payload = res?.freemium ?? res?.data?.freemium ?? res?.data ?? res;
      const next = normalizeFreemiumStatus(payload);
      setStatus(next);
      return next;
    } catch {
      setStatus(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const openPaywall = useCallback(() => setPaywallOpen(true), []);
  const closePaywall = useCallback(() => setPaywallOpen(false), []);

  const handleFreemiumError = useCallback(
    (err) => {
      if (!isFreemiumPaywallError(err)) return false;
      const next = normalizeFreemiumStatus({
        ...(err?.freemium || err?.data?.freemium || status || {}),
        unlimited: false,
        needsCarePass: true,
        paywallRequired: true,
      });
      setStatus(next);
      setPaywallOpen(true);
      return true;
    },
    [status]
  );

  return {
    status,
    loading,
    paywallOpen,
    openPaywall,
    closePaywall,
    refresh,
    handleFreemiumError,
  };
}
